/**
 * notifications.ts
 * Schedules local notifications for the logged-in officer:
 *   - 10 min and 5 min before shift end
 *   - 10 min and 5 min before each break ends
 *   - Immediately when secondary tasks are assigned
 *   - At shift change boundaries (06:55, 14:55, 22:55 ET)
 *
 * Android only (Capacitor). Silently no-ops on web / Electron.
 */

import { Capacitor } from '@capacitor/core'

const CHANNEL_ID      = 'castellan-alerts'
const CHANNEL_TASK_ID = 'castellan-tasks'

// ── Plugin loader ─────────────────────────────────────────────────────────────
async function getPlugin(): Promise<any | null> {
  if (!Capacitor.isNativePlatform()) return null
  try {
    const mod = await import('@capacitor/local-notifications' as any)
    return mod.LocalNotifications ?? null
  } catch { return null }
}

// ── Ensure notification channels exist (Android 8+) ──────────────────────────
async function ensureChannels(plugin: any): Promise<void> {
  try {
    await plugin.createChannel({
      id: CHANNEL_ID,
      name: 'Castellan Shift Alerts',
      description: 'Shift and break end warnings',
      importance: 5,
      vibration: true,
      sound: 'default',
      visibility: 1,
      lights: true,
      lightColor: '#b8862a',
    })
    await plugin.createChannel({
      id: CHANNEL_TASK_ID,
      name: 'Castellan Task Alerts',
      description: 'Secondary task and shift change alerts',
      importance: 5,
      vibration: true,
      sound: 'default',
      visibility: 1,
      lights: true,
      lightColor: '#3b82f6',
    })
  } catch { /* channels may already exist */ }
}

// ── Time helpers ──────────────────────────────────────────────────────────────
function parseMins(t: string | undefined): number | null {
  if (!t) return null
  const s = String(t).trim().replace(':', '')
  if (s.length === 3) return parseInt(s[0]) * 60 + parseInt(s.slice(1))
  if (s.length === 4) return parseInt(s.slice(0, 2)) * 60 + parseInt(s.slice(2))
  return null
}

function minsToDate(rawMins: number): Date {
  const mins = ((rawMins % 1440) + 1440) % 1440
  const h = Math.floor(mins / 60)
  const m = mins % 60
  const now = new Date()
  const target = new Date(now.getFullYear(), now.getMonth(), now.getDate(), h, m, 0, 0)
  if (target.getTime() <= now.getTime()) target.setDate(target.getDate() + 1)
  return target
}

// ── Notification IDs ──────────────────────────────────────────────────────────
// Shift end:    1001 (10 min), 1002 (5 min)
// Break 0-4:    2001-2042 (10/5 min each, 10 apart)
// Shift change: 3001 (morning), 3002 (evening), 3003 (night)
// Tasks:        4001-4010 (up to 10 tasks, immediate)

const ALL_NOTIF_IDS = [
  { id: 1001 }, { id: 1002 },
  { id: 2001 }, { id: 2002 }, { id: 2011 }, { id: 2012 },
  { id: 2021 }, { id: 2022 }, { id: 2031 }, { id: 2032 },
  { id: 2041 }, { id: 2042 },
  { id: 3001 }, { id: 3002 }, { id: 3003 },
  ...Array.from({ length: 10 }, (_, i) => ({ id: 4001 + i })),
]

// ── Public API ────────────────────────────────────────────────────────────────

export async function scheduleOfficerNotifications(
  callSign: string,
  shiftTo: string | undefined,
  breakSlots: { from?: string; to?: string }[],
  tasks: { task: string; detail?: string }[] = [],
): Promise<void> {
  const plugin = await getPlugin()
  if (!plugin) return

  const { display } = await plugin.requestPermissions()
  if (display !== 'granted') {
    console.warn('[notifications] Permission not granted')
    return
  }

  await ensureChannels(plugin)
  await plugin.cancel({ notifications: ALL_NOTIF_IDS })

  const toSchedule: any[] = []
  const nowMins = new Date().getHours() * 60 + new Date().getMinutes()

  // ── Shift end ─────────────────────────────────────────────────────────────
  const shiftEndMins = parseMins(shiftTo)
  if (shiftEndMins !== null) {
    toSchedule.push({
      id: 1001, channelId: CHANNEL_ID,
      title: '\u23F0 Shift Ending in 10 Minutes',
      body: 'Begin wrap-up. Prepare for end of shift.',
      schedule: { at: minsToDate(shiftEndMins - 10) },
    })
    toSchedule.push({
      id: 1002, channelId: CHANNEL_ID,
      title: '\u23F0 Shift Ending in 5 Minutes',
      body: 'Your shift ends in 5 minutes.',
      schedule: { at: minsToDate(shiftEndMins - 5) },
    })
  }

  // ── Break end ─────────────────────────────────────────────────────────────
  breakSlots.slice(0, 5).forEach((brk, i) => {
    const endMins = parseMins(brk.to)
    if (endMins === null) return
    const base = 2001 + i * 10
    toSchedule.push({
      id: base, channelId: CHANNEL_ID,
      title: '\u2615 Break Ending in 10 Minutes',
      body: 'Your break ends in 10 minutes. Return to post.',
      schedule: { at: minsToDate(endMins - 10) },
    })
    toSchedule.push({
      id: base + 1, channelId: CHANNEL_ID,
      title: '\u2615 Break Ends in 5 Minutes',
      body: 'Your break ends in 5 minutes.',
      schedule: { at: minsToDate(endMins - 5) },
    })
  })

  // ── Shift change boundaries (06:55, 14:55, 22:55) ────────────────────────
  // Schedule whichever is next and within 8 hours of now.
  const SHIFT_CHANGES = [
    { id: 3001, mins: 6*60+55,  label: 'Morning Shift Starting' },
    { id: 3002, mins: 14*60+55, label: 'Evening Shift Starting' },
    { id: 3003, mins: 22*60+55, label: 'Night Shift Starting' },
  ]
  for (const sc of SHIFT_CHANGES) {
    const diff = ((sc.mins - nowMins) + 1440) % 1440
    if (diff > 0 && diff <= 480) {
      toSchedule.push({
        id: sc.id, channelId: CHANNEL_TASK_ID,
        title: '\uD83D\uDD04 ' + sc.label,
        body: 'Shift change in 5 minutes. Prepare for handoff.',
        schedule: { at: minsToDate(sc.mins) },
      })
    }
  }

  // ── Secondary tasks (immediate, staggered 1.5s apart) ────────────────────
  tasks.slice(0, 10).forEach((task, i) => {
    toSchedule.push({
      id: 4001 + i, channelId: CHANNEL_TASK_ID,
      title: '\uD83D\uDCCB Task: ' + task.task,
      body: task.detail || 'You have been assigned a task.',
      schedule: { at: new Date(Date.now() + (i + 1) * 1500) },
    })
  })

  if (toSchedule.length === 0) {
    console.log(`[notifications] Nothing to schedule for ${callSign}`)
    return
  }

  await plugin.schedule({ notifications: toSchedule })
  console.log(`[notifications] Scheduled ${toSchedule.length} notification(s) for ${callSign}`)
}

export async function cancelAllNotifications(): Promise<void> {
  const plugin = await getPlugin()
  if (!plugin) return
  try {
    const pending = await plugin.getPending()
    if (pending?.notifications?.length > 0) {
      await plugin.cancel({ notifications: pending.notifications })
    }
  } catch { /* silent */ }
}

// ── Primary post change (immediate) ──────────────────────────────────────────
// Fired from the mobile hydrate cycle when the logged-in officer's post
// assignment changes mid-shift (supervisor reassigned them on desktop).
// ID 5001 — outside every scheduled-alert ID range so it never collides.
export async function notifyPostChange(post: string, time?: string): Promise<void> {
  const plugin = await getPlugin()
  if (!plugin) return
  try {
    const { display } = await plugin.requestPermissions()
    if (display !== 'granted') return
    await ensureChannels(plugin)
    await plugin.schedule({ notifications: [{
      id: 5001, channelId: CHANNEL_TASK_ID,
      title: '\uD83D\uDCCD Post Change: ' + post,
      body: time ? `You are now assigned to ${post} (${time}).` : `You are now assigned to ${post}.`,
      schedule: { at: new Date(Date.now() + 500) },
    }] })
    console.log(`[notifications] Post change notification fired: ${post}`)
  } catch { /* silent */ }
}
